import React from 'react';
import * as Sentry from '@sentry/react';

/**
 * Architecture Rule 36 + Rule 48. Wraps the app's route tree.
 *
 * - Catches React render exceptions and shows a recoverable UI instead of
 *   white-screen.
 * - Reports the captured error to Sentry with `request_id` (Rule 28) so it
 *   correlates with backend logs. If Sentry isn't initialized (DSN unset),
 *   the SDK no-ops and only the console.error fires.
 * - Surfaces the last known request_id to the user so support can stitch
 *   the report to backend logs (audit_logs / debug.queries / outbound_http_logs).
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, info: null, eventId: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    const requestId = (typeof window !== 'undefined' && window.__lastRequestId) || null;

    // Rule 48 — push to Sentry with route + request_id context.
    const eventId = Sentry.captureException(error, {
      tags: requestId ? { request_id: requestId } : undefined,
      contexts: {
        react: {
          componentStack: info?.componentStack,
        },
      },
    });

    this.setState({ info, eventId });
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, info);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleReset = () => {
    this.setState({ hasError: false, error: null, info: null, eventId: null });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const requestId = (typeof window !== 'undefined' && window.__lastRequestId) || null;
    const message = this.state.error?.message || 'An unexpected error occurred.';
    const { eventId } = this.state;

    return (
      <div className="auth-scene p-6">
        <div className="w-full max-w-md rounded-2xl border border-[var(--border)] bg-[var(--surface)] p-6 shadow-[var(--shadow-lg)]">
          <h1 className="text-xl font-semibold page-title">Something went wrong</h1>
          <p className="mt-2 text-sm page-subtitle">
            The app hit an unexpected error. You can try again, or reload the page if the problem persists.
          </p>
          <pre className="mt-3 max-h-32 overflow-auto rounded-lg border border-[var(--border)] bg-[var(--bg-muted)] p-3 text-xs page-title">
            {message}
          </pre>
          {requestId && (
            <p className="mt-3 text-xs page-subtitle">
              Request ID: <span className="font-mono">{requestId}</span>
            </p>
          )}
          {eventId && (
            <p className="mt-1 text-xs page-subtitle">
              Error ID: <span className="font-mono">{eventId}</span>
            </p>
          )}
          <div className="mt-4 flex gap-2">
            <button
              type="button"
              onClick={this.handleReset}
              className="flex-1 rounded-xl border border-[var(--border)] px-4 py-2 text-sm font-semibold page-subtitle hover:bg-[var(--bg-muted)]"
            >
              Try again
            </button>
            <button
              type="button"
              onClick={this.handleReload}
              className="flex-1 rounded-xl bg-[var(--accent)] px-4 py-2 text-sm font-semibold text-white hover:brightness-110"
            >
              Reload the app
            </button>
          </div>
        </div>
      </div>
    );
  }
}
